import { Injectable, Injector } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';
import { OAuthService } from './oauth.service';
import { AuthService } from './auth.service';

const TOKEN_REFRESH_INTERVAL_MS = 60_000;
const MIN_TOKEN_VALIDITY_SECONDS = 70;

/**
 * Failsafe ceiling for keycloak.init() when silent SSO is enabled.
 * keycloak-js's #checkSsoSilently() has NO internal timeout: if the silent-check
 * iframe is blocked (Keycloak frame-ancestors CSP, third-party-cookie blocking in
 * Safari/Chrome, or Keycloak unreachable) it waits forever, hanging APP_INITIALIZER
 * and leaving the splash screen up permanently. This bounds that wait so the app
 * always boots and falls back to the guard-driven redirect login.
 */
const INIT_TIMEOUT_MS = 8000;
/**
 * Wraps keycloak-js to provide Authorization Code Flow with PKCE.
 * Tokens are synced into localStorage via OAuthService so the existing
 * AuthInterceptor, AuthGuard, and sibling-app (DMS/GIS) token-passing continue
 * to work without changes.
 *
 * The service is enabled only when both environment.oauth and
 * environment.new_login_url are set (i.e. deployed envs). In local dev
 * (no new_login_url) the built-in password-grant form is used instead.
 */
@Injectable({ providedIn: 'root' })
export class KeycloakService {
  private keycloak: Keycloak | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  // AuthService is resolved lazily via Injector to avoid an NG0200 circular DI
  // cycle (AuthService injects KeycloakService and vice-versa). onAuthLogout only
  // needs it well after construction, so lazy resolution breaks the cycle.
  constructor(
    private oauthService: OAuthService,
    private injector: Injector,
  ) {}

  /**
   * True when the PKCE flow should be used for this environment.
   * Requires environment.oauth with an absolute Keycloak URL.
   */
  isEnabled(): boolean {
    return !!(environment.oauth && environment.oauth.origin?.startsWith('http'));
  }

  /**
   * Initialise keycloak-js with PKCE S256.
   * Must be called during APP_INITIALIZER before routes activate.
   *
   * keycloak-js points directly at the real Keycloak server (environment.oauth.origin)
   * so the interactive login form's cookies (KC_RESTART, AUTH_SESSION_ID) live on the
   * Keycloak domain — proxying through localhost breaks those cookies.
   *
   * Uses iframe-based silent SSO (silentCheckSsoRedirectUri). This lets an existing
   * Keycloak session authenticate the user without any visible redirect.
   *
   * IMPORTANT: this requires the Keycloak realm's Content-Security-Policy to include
   * the app origin in `frame-ancestors`, e.g.:
   *   frame-ancestors 'self' https://stg.whitehelmet.sa https://home.whitehelmet.sa;
   * Set this in Keycloak Admin → Realm Settings → Security Defenses → Content-Security-Policy.
   * Without it the silent-check iframe is blocked and the check can never succeed.
   *
   * keycloak-js's silent check has no internal timeout, so we bound init() ourselves
   * (INIT_TIMEOUT_MS): on CSP block / third-party-cookie block / Keycloak outage we
   * stop waiting and boot the app unauthenticated, letting the guard-driven login()
   * redirect take over. silentCheckSsoFallback also lets keycloak-js fall back to a
   * redirect when third-party cookies are unavailable.
   *
   * On load, init() processes the `?code&state` callback from the URL and
   * exchanges it for tokens. Resolves false (no redirect) when unauthenticated so
   * bootstrap is never blocked and the URL-token flow / public routes still work.
   */
  async init(): Promise<boolean> {
    if (!this.isEnabled()) {
      return false;
    }

    const { origin, realm, client_id } = environment.oauth;

    this.keycloak = new Keycloak({ url: origin, realm, clientId: client_id });

    this.keycloak.onTokenExpired = () => {
      this._refreshToken();
    };

    // Fired when Keycloak detects the SSO session has ended elsewhere (logout from
    // the account console at /realms/<realm>/account, or another app in the realm).
    // Requires checkLoginIframe: true — the check-session iframe polls Keycloak and
    // calls this when the session is no longer valid. Assigned BEFORE init() so an
    // early event is never missed.
    this.keycloak.onAuthLogout = () => {
      console.warn('[Keycloak] SSO session ended, redirecting to login');
      // clear any local storage related to the auth (lazy lookup breaks the DI cycle)
      this.injector.get(AuthService).clearLocalAuthState();
      this.login();
    };

    try {
      const authenticated = await this._withInitTimeout(
        this.keycloak.init({
          pkceMethod: 'S256',
          // Enables the check-session iframe + polling that drives onAuthLogout for
          // remote/cross-app logout. Requires the app origin in Keycloak's CSP
          // frame-ancestors (already configured). Bounded by _withInitTimeout below.
          checkLoginIframe: true,
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          silentCheckSsoFallback: true,
        }),
      );

      if (authenticated && this.keycloak.token) {
        this._syncTokens();
        this._startRefreshTimer();
      }

      return authenticated;
    } catch (err) {
      console.error('[KeycloakService] init error', err);
      return false;
    }
  }

  /**
   * Race keycloak.init() against a failsafe timeout. keycloak-js's silent-SSO and
   * check-session iframes have no internal timeout, so a blocked iframe, slow network,
   * or unreachable Keycloak would otherwise hang APP_INITIALIZER forever (splash never
   * hides). Whichever settles first wins: a timeout (or any init error) resolves to
   * `false` so bootstrap continues and the guard-driven redirect login takes over.
   */
  private _withInitTimeout(initPromise: Promise<boolean>): Promise<boolean> {
    let timer!: ReturnType<typeof setTimeout>;

    const failsafe = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => {
        console.warn(
          `[KeycloakService] init() exceeded ${INIT_TIMEOUT_MS}ms — booting unauthenticated`,
        );
        resolve(false);
      }, INIT_TIMEOUT_MS);
    });

    const initOrFalse = initPromise.catch((err) => {
      console.error('[KeycloakService] init error', err);
      return false;
    });

    return Promise.race([initOrFalse, failsafe]).finally(() => clearTimeout(timer));
  }

  /**
   * Trigger the Keycloak login redirect with PKCE.
   * @param redirectUri Full URL to redirect to after login. Defaults to current page.
   */
  login(redirectUri?: string): Promise<void> {
    if (!this.keycloak) {
      return Promise.reject(new Error('[KeycloakService] Not initialised'));
    }
    const lang = localStorage.getItem('lang') || 'en';
    return this.keycloak.login({
      redirectUri: redirectUri ?? window.location.href,
      locale: lang,
    });
  }

  /**
   * Trigger the Keycloak end-session redirect and clear local state.
   * The client_id used at login is the same one keycloak-js uses here
   * (per the spec: "logout client_id must match login client_id").
   */
  logout(redirectUri?: string, idTokenHint?: string): Promise<void> {
    if (!this.keycloak) {
      return Promise.reject(new Error('[KeycloakService] Not initialised'));
    }
    this._stopRefreshTimer();
    // keycloak-js only adds `id_token_hint` to the logout URL when it holds the ID
    // token in memory. After a page reload (or a URL/localStorage token redirect)
    // that in-memory token is gone, so Keycloak shows its "Do you want to log out?"
    // confirmation page. Restoring the hint here keeps logout silent & consistent.
    if (idTokenHint && !this.keycloak.idToken) {
      this.keycloak.idToken = idTokenHint;
    }
    const lang = localStorage.getItem('lang') || 'en';
    const fallback = `${window.location.origin}/sign-in?accept-language=${lang}&logout=true`;
    return this.keycloak.logout({ redirectUri: redirectUri ?? fallback });
  }

  /** True when keycloak-js considers the user authenticated. */
  get isAuthenticated(): boolean {
    return this.keycloak?.authenticated ?? false;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Copy keycloak-js tokens into localStorage (used by interceptor & sibling apps). */
  private _syncTokens(): void {
    if (!this.keycloak) return;
    if (this.keycloak.token) {
      this.oauthService.setAccessToken(this.keycloak.token);
    }
    if (this.keycloak.idToken) {
      localStorage.setItem('id_token', this.keycloak.idToken);
    }
  }

  private async _refreshToken(): Promise<void> {
    if (!this.keycloak) return;
    try {
      const refreshed = await this.keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS);
      if (refreshed) {
        this._syncTokens();
      }
    } catch {
      console.warn('[KeycloakService] Token refresh failed — redirecting to login');
      this._stopRefreshTimer();
      this.oauthService.setAccessToken('');
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
      this.login(window.location.href);
    }
  }

  private _startRefreshTimer(): void {
    this._stopRefreshTimer();
    this.refreshTimer = setInterval(() => {
      this._refreshToken();
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  private _stopRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
